import {
  Button,
  Drawer,
  useMediaQuery,
} from "@mui/material";
import { StyledEngineProvider, ThemeProvider, Theme } from "@mui/material/styles";
import { makeStyles, createStyles } from "@mui/styles";
import {  } from "@mui/styles";
import { Scafold } from "src/framework/layouts";
import { Loader, Confirmation } from "src/framework/components/commons";
import theme from "./theme/light";
import { Notifier } from "./framework/components/commons/dialogs";
import Scrollbars from "react-custom-scrollbars-2";
import { useObservable } from "./framework/store/storeUtil";
import "./App.css";
import { FC, useEffect, useMemo } from "react";
import { LocalStorage } from "./framework/commons/LocalStorage";
import jwtDecode from "jwt-decode";
import { getCategorias, getComponentes, getContaUsuario, getEstruturas, getParametros, padronizaSegmento, padronizaVenda, segmento$, tipoVenda$ } from "./store/global.store";
import { ApplicationBar, Menu } from "./framework/components/navigations";
import { CarrinhoStore } from "./components/loja/Carrinho/store";
import { PermissionUtil } from "./framework/commons/PermissionUtil";
import jwt from "jwt-simple";
import { getUserExtraData, isLoggedIn$, permissoes$ } from "./framework/store/LoginStore";
import { isLoading$, openMenu$ } from "./framework/store/AppStore";
import * as Sentry from "@sentry/react";

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

const replayEnabled = process.env.REACT_APP_SENTRY_REPLAY?.toUpperCase() === "TRUE";

Sentry.init({
  dsn: String(process.env.REACT_APP_SENTRY_URL),
  // Setting this option to true will send default PII data to Sentry. For example, automatic IP address collection on events
  sendDefaultPii: process.env?.REACT_APP_SENTRY_SEND_PII?.toUpperCase() === 'TRUE',
  // Filters
  ignoreErrors: process.env.REACT_APP_SENTRY_IGNORE_ERRORS?.split(";")?.map(i => i.trim())?.filter(Boolean) || [],
  // Integrations
  integrations: [...(replayEnabled ? [Sentry.replayIntegration()] : [])],
  // Tracing
  tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACES_RATE) ?? 0, //  Capture 100% of the transactions
  // Replay
  ...(replayEnabled && {
    replaysSessionSampleRate: Number(process.env.REACT_APP_SENTRY_REPLAY_SESSION_SAMPLE_RATE ?? "0.05"),
    replaysOnErrorSampleRate: Number(process.env.REACT_APP_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE ?? "1.0"),
  }),
  // Environment
  environment: process.env.REACT_APP_ENV
});

const drawerWidth = 0;

const useStyles = makeStyles((_theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      height: "100%"
    },
    appBar: {
      zIndex: theme.zIndex.drawer + 1,
    },
    drawerPaper: {
      width: drawerWidth,
    },
    drawerContainer: {
      overflow: "auto",
      marginBottom: "40px",
    },
    menuButton: {
      marginRight: theme.spacing(2),
    },
    hide: {
      display: "none",
    },
    acticeLink: {
      color: "inherit",
      textDecoration: "inherit",
    },
    menuItemHeader: theme.mixins.toolbar,
    menuFooter: {
      width: "288px",
      minHeight: "40px",
      backgroundColor: "white",
      display: "block",
      position: "absolute",
      bottom: "0",
    },
    content: {
      flexGrow: 1,
      height: "100%",
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      marginLeft: drawerWidth,
    },
    contentShift: {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    },
    segmento: {
      color: theme.palette.secondary.main
    },
    segmento_selecionado: {
      borderBottom: `2px solid ${theme.palette.primary.main} !important`,
      borderRadius: "0px"
    },
    menuBox: {
      margin: "20px 0px",
      textAlign: "center",
      maxWidth: "100vw",
    },
  })
);

const App: FC = () => {
    const classes = useStyles();

    const segmento = useObservable(segmento$);
    const tipoVenda = useObservable(tipoVenda$);
    const isLoggedIn = useObservable(isLoggedIn$);
    const isLoading = useObservable(isLoading$);
    const openMenu = useObservable(openMenu$);

    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
      const storageAvailable = LocalStorage.isAvailable();
      if (!storageAvailable) {
        Sentry.captureMessage("LocalStorage indisponível", {
            level: "warning",
            tags: {
              type: "local_storage",
              operation: "availability_check"
            }
          }
        );
      }
    }, []);

    useEffect(()=>{
      if (segmento) {
        LocalStorage.set("solar.ultimo_segmento_selecionado", padronizaSegmento(segmento))
      }
    },[segmento])

    useEffect(()=>{
      if (tipoVenda) {
        LocalStorage.set("solar.tipo_venda_selecionada", padronizaVenda(tipoVenda))
      }
    },[tipoVenda])

    const handleRedirectProjetos = () => {
      const _tokenApi = LocalStorage.get("solar.token");
      if (_tokenApi) {
        const decoded: any = jwtDecode(_tokenApi);
        const _tokenProjetos = {
          cnpj_cpf: decoded.user.cnpjCpf,
          token: decoded.token,
          refresh_token: decoded.refreshToken,
          access_token: decoded.token,
          type: decoded.type,
          customContaUnica: decoded.customContaUnica
        }

        const tProjetos = jwt.encode(_tokenProjetos, (process.env.REACT_APP_TOKEN_KEY || ''));

        const _url = `token=${tProjetos}`;
        window.location.href = `${process.env.REACT_APP_URL_PROJETOS}?${_url}`;
        // UrlUtil.goto(`${process.env.REACT_APP_URL_PROJETOS}`);
      }
    }

    const onInit = async () => {
      if (isLoggedIn) {
          getUserExtraData();
          getContaUsuario();
          getEstruturas();
          getComponentes();
          getParametros();
          getCategorias();
          await CarrinhoStore.iniciar();
      }
    };

    const onExit = () => {
    };

    const handleClose = () => {
      openMenu$.next(false);
    };

    const vendaAvulso = useMemo(() => {
      return tipoVenda === 'avulso';
    }, [tipoVenda]);

    return (
      <StyledEngineProvider injectFirst>
        <Scrollbars key="app-scrollbar" style={{ width: "100vw", height: "100vh" }} >
          <div className="App">
            <fieldset disabled={isLoading} className="global-fieldset">
              <Scafold 
                onExit={() => onExit()} 
                onInit={() => onInit()} 
                appBar={<>
                  <ApplicationBar
                    logo={<></>}
                    classes={classes} 
                    disableMenu={!isMobile}
                  >
                    {!isMobile ? (
                      <>
                        <Button variant="text" className={classes.segmento} onClick={() => {
                          handleRedirectProjetos()
                        }}>Plataforma Solar</Button>
                        {PermissionUtil.can(PermissionUtil.PERMISSION_LOJA_ONGRID, permissoes$.value) &&
                          <Button 
                            className={(segmento === "on" && !vendaAvulso) ? classes.segmento_selecionado : classes.segmento}
                            variant="text" color="primary"
                            onClick={() => {
                              segmento$.next("on"); 
                              tipoVenda$.next("gerador");
                            }}>
                              Loja On Grid
                          </Button>
                        }
                        {PermissionUtil.can(PermissionUtil.PERMISSION_LOJA_OFFGRID, permissoes$.value) &&
                          <Button 
                            className={(segmento === "off" && !vendaAvulso) ? classes.segmento_selecionado : classes.segmento}
                            variant="text" color="primary"
                            onClick={() => {
                              segmento$.next("off"); 
                              tipoVenda$.next("gerador");
                            }}>
                              Loja Off Grid
                          </Button>
                        }
                        {PermissionUtil.can(PermissionUtil.PERMISSION_LOJA_AVULSO, permissoes$.value) &&
                          <Button 
                            className={vendaAvulso ? classes.segmento_selecionado : classes.segmento}
                            variant="text" color="primary"
                            onClick={() => {
                              segmento$.next(null); 
                              tipoVenda$.next("avulso");
                            }}>
                              Avulso
                          </Button>
                        }
                      </>
                    ) : (<></>)}
                  </ApplicationBar>
                    { isMobile && (
                      <Drawer
                        variant="temporary"
                        anchor="left"
                        open={openMenu}
                        onClose={handleClose}
                        ModalProps={{
                          keepMounted: true, // melhora performance no mobile
                        }}
                        PaperProps={{
                          style: {
                            width: (window.innerWidth*0.8),
                            backgroundColor: "#E2E2E2",
                            overflow: "hidden",
                          },
                        }}
                      >
                        <Menu classes={classes} handleRedirectProjetos={handleRedirectProjetos} onClose={handleClose} />
                      </Drawer>
                    )}
                </>}
              />
            </fieldset>
          </div>
        </Scrollbars>
        <Loader />
        <Confirmation />
      </StyledEngineProvider>
    );
};

export default App;
